<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import api from '@/services/api'
  import { useToast } from 'primevue/usetoast'
  import { INote } from '@/models/note/interfaces'
  import Button from 'primevue/button'
  import NoteComponent from './NoteComponent.vue'
  import { i18n } from '@/i18n'
  import PermissionRole from '@/components/PermissionRole/PermissionComponent.vue'
  import { useBreakpointsStore, useLeadStore, useUserStore } from '@/store'
  import { captureException } from '@sentry/vue'
  import EditorContent from '@/components/EditorNotes/EditorContent.vue'
  import { EditorComponent } from '@/components/EditorNotes/editorInterface'

  type Iprops = {
    projectId: string
  }

  const props = defineProps<Iprops>()
  const { t } = i18n.global
  const projectId = computed(() => {
    return props.projectId
  }).value

  const userStore = useUserStore()
  const { zohoIntegration } = storeToRefs(userStore)
  const leadStore = useLeadStore()
  const { fetchWorkContacts } = useLeadStore()
  const { projectWorkContacts, project } = storeToRefs(leadStore)
  const showCreateNote = ref(false)
  const getWorkContacts = async () => {
    await fetchWorkContacts()
  }
  const breakpointsStore = useBreakpointsStore()
  const { md } = storeToRefs(breakpointsStore)

  const toast = useToast()

  interface IEditedNote {
    id: string
    Note_Title: string
    Note_Content: string
  }

  const clearHtml = (html = '') => {
    const regex = /(<([^>]+)>)/gi
    return html.replace(regex, '')
  }

  const notes = ref<INote[]>([])
  const submitLoading = ref(false)
  const noteReply = ref<INote | null>(null)
  const noteInfo = ref('')
  const editorChild = ref<EditorComponent | null>(null)

  function updateNoteAfterEdit(editedNote: IEditedNote) {
    const note = notes.value.find((n) => n.id === editedNote.id)
    if (!note) return
    note.Note_Title = editedNote.Note_Title
    note.Note_Content = editedNote.Note_Content
  }

  function removeNoteAfterDelete(deletedNote: { id: string }) {
    const noteIndex = notes.value.findIndex((n) => n.id === deletedNote.id)
    if (noteIndex > -1) notes.value.splice(noteIndex, 1)
  }

  const updatecontent = (content: string) => {
    noteInfo.value = content
  }
  const handleOpenCreateNote = () => {
    showCreateNote.value = true
    noteReply.value = null
  }
  async function createNote() {
    if (clearHtml(noteInfo.value)?.trim()?.length === 0) return
    try {
      submitLoading.value = true
      const html = noteInfo.value
      const payload = noteReply.value
        ? {
            Note_Content: html,
            NoteReply: {
              ReplyZuidId: noteReply.value.Owner.zuid,
              ReplyCRMId: noteReply.value.Owner.id,
              ReplyAuthor: noteReply.value.Owner.name,
              RePlyContent: noteReply.value.Note_Content,
            },
          }
        : {
            Note_Content: html,
          }

      console.log('payload', payload)
      await api.post(`/Api/lead/${projectId}/Notes`, { ...payload })
      await fetchNotes()
      if (editorChild.value) {
        editorChild.value.commands.clearEditorContent()
      }
      noteReply.value = null
      noteInfo.value = ''
      toast.add({ severity: 'success', summary: i18n.global.t('projectNotes.success'), detail: i18n.global.t('projectNotes.noteAdded'), life: 3000 })
      submitLoading.value = false
      showCreateNote.value = false
    } catch (err) {
      captureException(err)
      submitLoading.value = false
      console.log('err', err)
      toast.add({ severity: 'error', summary: i18n.global.t('projectNotes.error'), detail: i18n.global.t('projectNotes.errorAddingNote'), life: 3000 })
    }
  }

  async function fetchNotes() {
    try {
      const response = await api.get(`/Api/Lead/${projectId}/Notes`)
      console.log('response', response.data.data.data)
      notes.value = [...response.data.data.data.data]
    } catch (err) {
      captureException(err)
      console.log('err', err)
    }
  }

  const handleReplyNote = (note: INote) => {
    noteReply.value = note
    showCreateNote.value = true
  }

  onMounted(async () => {
    await fetchNotes()
    await getWorkContacts()
  })
</script>

<template>
  <div class="col-12">
    <div class="flex align-items-center justify-content-between title-tab">
      <div class="flex text-xl align-items-center">
        <div class="flex pr-2">
          <i class="pi pi-list" />
        </div>
        <div class="flex text-xl">{{ t('projectNotes.notes') }}</div>
      </div>
      <div class="flex">
        <Button v-if="project.isAuthorized && !project.isClosed" :label="t('projectNotes.addNotes')" icon="pi pi-plus" class="p-button-text" @click="handleOpenCreateNote" />
      </div>
    </div>
    <div class="m-auto max-w-30rem">
      <div v-for="note in notes" :key="note.id" class="flex flex-row mb-3">
        <NoteComponent :note="note" @update-note="updateNoteAfterEdit" @delete-note="removeNoteAfterDelete" @reply-note="() => handleReplyNote(note)" />
      </div>

      <Dialog v-model:visible="showCreateNote" :position="!md ? 'bottom' : undefined" class="w-full m-0 hide-on-camera" style="max-width: 42rem" :modal="true" :draggable="false">
        <template #header>
          <h5>{{ t('projectNotes.addNote') }}</h5>
        </template>
        <PermissionRole :nodo-name="'leadsNotePermissions'" :key-name="'create'">
          <div v-if="zohoIntegration" class="w-full flex flex-column align-items-center gap-2">
            <div v-if="noteReply" class="w-full flex flex-row gap-1 container-reply">
              <div class="w-full flex flex-column gap-1">
                <p class="font-bold text-lg m-0 text-overflow-ellipsis white-space-nowrap overflow-hidden block" style="color: #3b82f6">
                  {{ noteReply?.Owner?.name }}
                </p>
                <p class="text-sm" style="color: #aeaeae">
                  {{ clearHtml(noteReply?.Note_Content) }}
                </p>
              </div>
              <Button icon="pi pi-times" class="p-button-rounded p-button-text" text rounded @click="noteReply = null" />
            </div>
            <div class="w-full flex flex-row align-items-center gap-2">
              <EditorContent
                ref="editorChild"
                :items-suggestions="projectWorkContacts"
                class="flex-1"
                :placeholder="i18n.global.t('projectNotes.noteEditor')"
                @create:note="createNote"
                @update:note="updatecontent"
              />
              <Button icon="pi pi-send" :loading="submitLoading" :disabled="clearHtml(noteInfo).trim().length === 0 ? true : submitLoading" @click="createNote" />
            </div>
          </div>
        </PermissionRole>
      </Dialog>
    </div>
  </div>
</template>

<style lang="css" scoped>
  .title-tab {
    min-height: 40px;
  }
  .off-screen {
    bottom: -80px;
    transition: bottom 0.3s;
  }
  .new-note-container {
    background-color: rgba(255, 255, 255, 0.3);
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(0, 0, 0, 0.2);
    margin-left: -1rem;
    margin-right: -1rem;
    width: calc(100% + 2rem);
    max-width: 32rem;
  }

  .container-reply {
    background-color: #83aff745;
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
    padding: 5px 10px;
    border-radius: 10px;
    /* #d1ddf1 */
    /* #83aff745 */
    /* border: 1px solid rgba(0, 0, 0, 0.2); */
  }

  @media only screen and (max-width: 991px) {
    .new-note-container {
      left: 0;
      right: 0;
      width: 100%;
      max-width: unset;
      margin-left: 0;
      margin-right: 0;
    }

    .off-screen.show {
      bottom: 0;
    }
  }

  @media only screen and (min-width: 992px) {
    .new-note-container {
      left: unset;
      right: unset;
      margin-left: -1rem;
      margin-right: -1rem;
      width: calc(100% + 2rem);
      max-width: 32rem;
    }

    .off-screen.show {
      bottom: 1rem;
    }
  }
</style>
